import Service, { service } from '@ember/service';

import Cookies from 'js-cookie';

import type { Service as ServiceTypes } from 'webapp-types';

export const LOCAL_STORAGE_KEY = 'forceOnboardingFlow920261';
export const WEBSITE_PROMPT_COOKIE = 'exp_920262_prompt';

enum FlowType {
	A = '920261_A',
	B = '920261_B',
	C = '920261_C',
}

export default class ExperimentGenieOnboarding920261Service extends Service {
	@service('account') declare accountService: ServiceTypes.Account;
	@service('current-user') declare currentUserService: ServiceTypes.CurrentUser;
	@service('onboarding') declare onboardingService: ServiceTypes.Onboarding;

	// Single key, explicit value ('A' | 'B') — setting one always replaces the other, so there's
	// no way to leave a stale flag from an earlier test session silently overriding a later one.
	private get forcedFlow(): FlowType | null {
		try {
			const value = localStorage.getItem(LOCAL_STORAGE_KEY);
			return value === FlowType.A || value === FlowType.B ? value : null;
		} catch (_e) {
			// Safari in Private Browsing Mode can throw on localStorage access
			return null;
		}
	}

	// Prompt text from the website's prompt signup; blank or whitespace-only counts as absent. Read
	// once per page load, so clearWebsitePrompt() only takes effect on the next one.
	readonly websitePrompt = Cookies.get(WEBSITE_PROMPT_COOKIE)?.trim() || null;

	// So a later signup in the same browser doesn't land in Flow C again. Removal only works when the
	// domain matches the one the website set it with: the app's cookie domain (".databox.com" in
	// production, none locally or on custom domains).
	clearWebsitePrompt(): void {
		Cookies.remove(WEBSITE_PROMPT_COOKIE, {
			domain: this.accountService.cookieAuthSID?.domain,
		});
	}

	// Cookie always wins over the forced/cohort A-vs-B split. Deliberately no isOnboarded guard:
	// the product requirement is only "non-template owner". Marketplace skill signups have their
	// own flow (OnboardingService#steps), so they're left out of the experiment.
	get isEligibleForFlowC(): boolean {
		return (
			!this.onboardingService.isEligibleForMarketplaceSkillFlow &&
			!!this.websitePrompt &&
			!this.onboardingService.templateId &&
			this.currentUserService.isOwner
		);
	}

	get experimentOrigin(): string {
		if (this.isEligibleForFlowC) {
			return FlowType.C;
		}

		return this.isEligibleForFlowB ? FlowType.B : FlowType.A;
	}

	private get isInFlowBCohort(): boolean {
		const accountId = Number(this.accountService.id);

		return Number.isFinite(accountId) && accountId % 2 !== 0;
	}

	get isEligibleForFlowB(): boolean {
		if (this.onboardingService.isEligibleForMarketplaceSkillFlow) {
			return false;
		}

		if (this.forcedFlow) {
			return this.forcedFlow === FlowType.B;
		}

		if (this.currentUserService.isOnboarded) {
			return false;
		}

		if (this.onboardingService.templateId) {
			return false;
		}

		if (!this.currentUserService.isOwner) {
			return false;
		}

		return this.isInFlowBCohort;
	}
}

declare module '@ember/service' {
	interface Registry {
		'experiment/genie-onboarding-920261': ExperimentGenieOnboarding920261Service;
	}
}
